import { createContext, useState, FC, ReactNode, useContext, useCallback } from "react";
import { AuthContext } from "./AuthContext";
import { getAllPayments } from "../utilities/api/adminService";

interface PaymentsAnalyticsContextValue {
  paymentsData: any[];
  totalPages: number;
  currentPage: number;
  perPage: number;
  totalItems: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  loading: boolean;
  fetchPaymentsData: (page?: number, filters?: any) => Promise<void>;
  setPerPage: (perPage: number) => void;
}

export const PaymentsAnalyticsContext = createContext<PaymentsAnalyticsContextValue>({
  paymentsData: [],
  totalPages: 1,
  currentPage: 1,
  perPage: 10,
  totalItems: 0,
  hasPreviousPage: false,
  hasNextPage: false,
  loading: false,
  fetchPaymentsData: async () => {},
  setPerPage: () => {},
});

export const PaymentsAnalyticsProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { accessToken } = useContext(AuthContext);
  const [paymentsData, setPaymentsData] = useState<any[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchPaymentsData = useCallback(
    async (page: number = 1, filters?: any) => {
      if (!accessToken) return;
      try {
        setLoading(true);
        const response = await getAllPayments(accessToken, page, perPage, filters);
        if (response.ok) {
          setPaymentsData(response.data.data);
          setTotalItems(response.data.total);
          setTotalPages(response.data.last_page);
          setCurrentPage(response.data.current_page);
          setHasPreviousPage(response.data.current_page > 1);
          setHasNextPage(response.data.current_page < response.data.last_page);
        }
      } catch (error: any) {
        console.error("Error fetching payments data:", error);
      } finally {
        setLoading(false);
      }
    },
    [accessToken, perPage]
  );

  return (
    <PaymentsAnalyticsContext.Provider
      value={{
        paymentsData,
        totalPages,
        currentPage,
        perPage,
        totalItems,
        hasPreviousPage,
        hasNextPage,
        loading,
        fetchPaymentsData,
        setPerPage,
      }}
    >
      {children}
    </PaymentsAnalyticsContext.Provider>
  );
};

export const usePaymentsAnalytics = () => {
  const context = useContext(PaymentsAnalyticsContext);
  if (!context) {
    throw new Error("usePaymentsAnalytics must be used within an PaymentsAnalyticsProvider");
  }
  return context;
};
